import {
  SortDirectionEnum,
  StudentListOrganizationSortableFieldEnum,
  StudentListBulkMessagesSortableFieldEnum,
  StudentListChannelOpensSortableFieldEnum,
  StudentListDirectMessagesSortableFieldEnum,
  StudentListFeedPostsSortableFieldEnum,
  StudentListGroupChatsSortableFieldEnum,
  StudentListLiveEventsSortableFieldEnum,
  StudentListProfileViewsSortableFieldEnum,
  StudentListRoommateMatchFriendFinderSearchesSortableFieldEnum,
  StudentListCommunityToolsSortableFieldEnum,
  StudentListCommitIndexSortableFieldEnum,
  StudentListRisePredictiveIndexSortableFieldEnum,
  StudentListFeedPostViewsSortableFieldEnum,
  StudentListRiseActionDetailsSortableFieldEnum,
} from "@/__generated__/types";
import { z } from "zod/v4";
import { ParsedQs } from "qs";
import { FilterContext, FilterContextType } from "./FilterContext";
import { StudentListMostEngagedSortableFieldEnum } from "@/__generated__/graphql";

// All fields that can be used for sorting
export type SortableFieldEnum = z.infer<typeof SortableField>;

// All fields a student query can be sorted by, regardless of context
export const SortableField = z.union([
  z.enum(StudentListBulkMessagesSortableFieldEnum),
  z.enum(StudentListChannelOpensSortableFieldEnum),
  z.enum(StudentListDirectMessagesSortableFieldEnum),
  z.enum(StudentListFeedPostsSortableFieldEnum),
  z.enum(StudentListFeedPostViewsSortableFieldEnum),
  z.enum(StudentListGroupChatsSortableFieldEnum),
  z.enum(StudentListLiveEventsSortableFieldEnum),
  z.enum(StudentListOrganizationSortableFieldEnum),
  z.enum(StudentListProfileViewsSortableFieldEnum),
  z.enum(StudentListRoommateMatchFriendFinderSearchesSortableFieldEnum),
  z.enum(StudentListMostEngagedSortableFieldEnum),
  z.enum(StudentListCommunityToolsSortableFieldEnum),
  z.enum(StudentListRisePredictiveIndexSortableFieldEnum),
  z.enum(StudentListRiseActionDetailsSortableFieldEnum),
]);

// Map of query context to ordering key
export const contextToOrderingKey: Record<FilterContextType, string | null> = {
  bulkMessages: "bulkMessageOrdering",
  organization: null,
  directMessages: "directMessagesOrdering",
  channelOpens: "channelOpensOrdering",
  communityTools: "communityToolsOrdering",
  roommateMatchFriendFinderSearches:
    "roommateMatchFriendFinderSearchesOrdering",
  feedPosts: "feedPostsOrdering",
  feedPostViews: "feedPostViewsOrdering",
  groupChats: "groupChatsOrdering",
  liveEvents: "liveEventsOrdering",
  mostEngaged: "mostEngagedOrdering",
  profileViews: "profileViewsOrdering",
  commitIndex: "commitIndexOrdering",
  funnelChart: null,
  liveEvent: null,
  rise: null,
  search: null,
  "rise.actionDetails": null,
  "rise.commitIndex": null,
  "rise.applyIndex": null,
  "rise.meltIndex": null,
  "rise.retainIndex": null,
  "rise.yieldIndex": null,
};

// Map of filter context to context specific ordering fields
export const contextToOrderingEnum: Record<FilterContextType, string[]> = {
  bulkMessages: Object.values(StudentListBulkMessagesSortableFieldEnum),
  channelOpens: Object.values(StudentListChannelOpensSortableFieldEnum),
  commitIndex: Object.values(StudentListCommitIndexSortableFieldEnum),
  communityTools: Object.values(StudentListCommunityToolsSortableFieldEnum),
  directMessages: Object.values(StudentListDirectMessagesSortableFieldEnum),
  feedPosts: Object.values(StudentListFeedPostsSortableFieldEnum),
  feedPostViews: Object.values(StudentListFeedPostViewsSortableFieldEnum),
  funnelChart: [],
  groupChats: Object.values(StudentListGroupChatsSortableFieldEnum),
  liveEvent: Object.values(StudentListOrganizationSortableFieldEnum),
  liveEvents: Object.values(StudentListLiveEventsSortableFieldEnum),
  mostEngaged: Object.values(StudentListMostEngagedSortableFieldEnum),
  organization: Object.values(StudentListOrganizationSortableFieldEnum),
  profileViews: Object.values(StudentListProfileViewsSortableFieldEnum),
  roommateMatchFriendFinderSearches: Object.values(
    StudentListRoommateMatchFriendFinderSearchesSortableFieldEnum,
  ),
  search: [],
  rise: Object.values(StudentListRisePredictiveIndexSortableFieldEnum),
  "rise.actionDetails": Object.values(
    StudentListRiseActionDetailsSortableFieldEnum,
  ),
  "rise.commitIndex": [],
  "rise.applyIndex": [],
  "rise.meltIndex": [],
  "rise.retainIndex": [],
  "rise.yieldIndex": [],
};

export const StudentSortQueryUrlParams = z.object({
  direction: z.nullable(z.enum(SortDirectionEnum)).default(null),
  field: z.nullable(SortableField).default(null),
});

export function isStudentListOrganizationSortableFieldEnum(
  value: SortableFieldEnum,
): value is StudentListOrganizationSortableFieldEnum {
  return Object.values(StudentListOrganizationSortableFieldEnum).includes(
    value as StudentListOrganizationSortableFieldEnum,
  );
}

export type OrderingInput =
  | Record<
      string,
      {
        direction: SortDirectionEnum;
        field: SortableFieldEnum;
      }
    >
  | {
      direction: SortDirectionEnum;
      field: StudentListOrganizationSortableFieldEnum;
    };

/**
 * class StudentOrdering
 * - Determines if a context + ordering is valid or not
 * - Convert to and from URL params
 * - Formats ordering data for student query
 */
export class StudentOrdering {
  readonly field: SortableFieldEnum | null;
  readonly direction: SortDirectionEnum | null;
  readonly context: FilterContextType;

  constructor(
    field: SortableFieldEnum | null,
    direction: SortDirectionEnum | null,
    context: FilterContextType = "organization",
  ) {
    this.field = field;
    this.direction = direction;
    this.context = context;

    if (field !== null && !StudentOrdering.isValidSortField(field, context))
      this.throwInvalidError(field, context);
  }

  static fromUrlQuery(parameters: ParsedQs): StudentOrdering | null {
    const orderingInput = parameters["ordering"];
    const context = FilterContext.fromUrlQuery(parameters).data;

    if (orderingInput) {
      const orderingParseResult =
        StudentSortQueryUrlParams.safeParse(orderingInput);
      if (orderingParseResult.success) {
        const data = orderingParseResult.data;
        return new StudentOrdering(data.field, data.direction, context);
      } else {
        console.error(`Could not parse ordering from url query.`);
      }
    }
    return new StudentOrdering(null, null, context);
  }

  toUrlQuery() {
    return {
      ordering: { direction: this.direction, field: this.field },
    };
  }

  // build query input from field, direction, and context
  getOrderingQueryInput(): OrderingInput | null {
    if (!this.direction || !this.field) return null;

    const orderingKey = contextToOrderingKey[this.context];
    if (orderingKey === null) {
      return {
        direction: this.direction,
        field: this.field as StudentListOrganizationSortableFieldEnum,
      };
    } else {
      const isOrganizationField = isStudentListOrganizationSortableFieldEnum(
        this.field,
      );

      if (isOrganizationField) {
        return {
          organizationOrdering: {
            direction: this.direction,
            field: this.field,
          },
        } as OrderingInput;
      }
      return {
        [orderingKey]: {
          direction: this.direction,
          field: this.field,
        },
      } as OrderingInput;
    }
  }

  static isValidSortField(
    field: SortableFieldEnum,
    context: FilterContextType,
  ) {
    if (isStudentListOrganizationSortableFieldEnum(field)) return true;

    const values = contextToOrderingEnum[context] || [];
    return values.includes(field);
  }

  clone({
    field = this.field,
    direction = this.direction,
    context = this.context,
  }: {
    field?: SortableFieldEnum | null;
    direction?: SortDirectionEnum | null;
    context?: FilterContextType;
  }) {
    if (field !== null && !StudentOrdering.isValidSortField(field, context))
      this.throwInvalidError(field, context);
    return new StudentOrdering(field, direction, context);
  }

  throwInvalidError(field: SortableFieldEnum, context: FilterContextType) {
    throw Error(
      `Invalid sort field and context combindation: Cannot sort students by '${field}' with the context '${context}'`,
    );
  }
}
